import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import FormField from '../../../elements/FormField';
import type { AdFormData, Campaign, CampaignCreationStep } from '../../../types';
import { useAppDispatch } from '../../../hooks/useAppDipatch';
import { setCreationStep } from '../../../features/campaigns/campaignsSlice';

interface OfferCreationStepProps {
  defaultValues: AdFormData | null;
  campaign: Campaign;
  onSubmit: (offerData: AdFormData) => void;
}

const initialOfferData: AdFormData = {
  name: '',
  headline: '',
  terms: '',
  disclaimer: '',
  destination: '', // 'landing_page', 'quiz', 'phone_pool'
};

export default function OfferCreationForm({ defaultValues, campaign, onSubmit }: OfferCreationStepProps) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const [previousStep, setPreviousStep] = useState<CampaignCreationStep>('adset');
  const [formData, setFormData] = useState<AdFormData>(defaultValues ?? initialOfferData);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const isValid = () => {
    const { name, headline, terms, disclaimer, destination } = formData;
    return !!name && !!headline && !!terms && !!disclaimer && !!destination;
  };

  const onBackHandle = () => {
    if (previousStep === 'adset') {
      dispatch(setCreationStep('adset'));
    } else {
      dispatch(setCreationStep('none'));
    }
  };

  useEffect(() => {
    if (location.pathname.includes(campaign.id)) {
      setPreviousStep('none');
    }
  }, [location]);

  return (
    <div className="glass-card rounded-xl">
      <div className="border-b border-gray-700/50 p-6">
        <h2 className="text-2xl font-semibold text-neutral-light">Create Offer</h2>
        <p className="mt-2 text-text-muted">Design the consumer-facing legal guarantee.</p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        <section>
          <h3 className="text-lg font-semibold text-neutral-light mb-4">Offer Details</h3>
          <FormField label="Offer Name" required highlight>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
              placeholder="e.g. Free Case Evaluation"
            />
          </FormField>

          <FormField label="Headline / Promise" required highlight>
            <input
              type="text"
              value={formData.headline}
              onChange={(e) => setFormData({ ...formData, headline: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
              placeholder="e.g. No Fee Unless We Win Your Case."
            />
          </FormField>

          <FormField label="Terms" required highlight>
            <textarea
              value={formData.terms}
              onChange={(e) => setFormData({ ...formData, terms: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
              placeholder="e.g. Applies to contingency-fee agreements only."
              rows={3}
            />
          </FormField>

          <FormField label="Disclaimer" required highlight>
            <textarea
              value={formData.disclaimer}
              onChange={(e) => setFormData({ ...formData, disclaimer: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
              placeholder="e.g. Results vary. Court approval required."
              rows={2}
            />
          </FormField>
        </section>

        <section>
          <h3 className="text-lg font-semibold text-neutral-light mb-4">Destination</h3>
          <FormField label="Select a Destination" required>
            <select
              value={formData.destination}
              onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
              className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light focus:outline-none focus:border-accent/50"
            >
              <option value="">Select an option...</option>
              <option value="landing_page">Landing Page</option>
              <option value="quiz">Quiz</option>
              <option value="phone_pool">Phone Pool</option>
            </select>
          </FormField>

          {formData.destination === 'landing_page' && (
            <FormField label="URL" required highlight>
              <input
                type="url"
                value={formData.url || ''}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
                placeholder="https://example.com/landing-page"
              />
            </FormField>
          )}

          {formData.destination === 'quiz' && (
            // A simplified placeholder for the quiz builder
            <div className="p-4 bg-brand-hover rounded-lg mt-4 text-text-muted">
              Quiz builder will appear here to define questions.
            </div>
          )}

          {formData.destination === 'phone_pool' && (
            <FormField label="Phone Number" required highlight>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 bg-gray-700/50 border-2 border-gray-700/50 rounded-lg text-neutral-light placeholder-text-muted focus:outline-none focus:border-accent/50"
                placeholder="(123) 456-7890"
              />
            </FormField>
          )}
        </section>

        <div className="pt-6 border-t border-gray-700/50">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={onBackHandle}
              className="px-6 py-2 text-sm text-text-muted hover:text-neutral-light transition-colors"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={!isValid()}
              className="flex items-center gap-2 px-6 py-2 bg-accent hover:bg-accent-hover disabled:bg-gray-700 disabled:cursor-not-allowed text-brand-bg font-medium rounded-lg transition-colors"
            >
              Create Offer
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
